import React from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { useCampaigns } from '../../contexts/CampaignContext';
import SafeIcon from '../../common/SafeIcon';
import { FiEdit, FiMail, FiMoreVertical, FiPause, FiPhone, FiPlay, FiTrash2 } from 'react-icons/fi';


const CampaignList = ({ campaigns, onEditCampaign }) => {
  const { deleteCampaign, launchCampaign, pauseCampaign, loading } = useCampaigns();

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type) => {
    return type === 'email' ? FiMail : FiPhone;
  };

  if (campaigns.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
        <SafeIcon icon={FiMail} className="text-gray-300 text-4xl mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns found</h3>
        <p className="text-gray-500">Create your first campaign to start reaching out to your contacts.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Campaign
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Recipients
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Performance
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {campaigns.map((campaign, index) => (
              <motion.tr
                key={campaign.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="table-row"
              >
                <td className="px-6 py-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                    <div className="text-sm text-gray-500">{campaign.description}</div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <SafeIcon 
                      icon={getTypeIcon(campaign.type)} 
                      className="text-gray-400 mr-2" 
                    />
                    <span className="text-sm text-gray-900 capitalize">{campaign.type}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(campaign.status)}`}>
                    {campaign.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {campaign.recipients?.length || 0}
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">
                    {campaign.stats.sent > 0 ? (
                      <div>
                        <div>Sent: {campaign.stats.sent}</div>
                        <div className="text-xs text-gray-500">
                          Opened: {campaign.stats.opened} ({campaign.stats.sent > 0 ? Math.round((campaign.stats.opened / campaign.stats.sent) * 100) : 0}%)
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-500">No data</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {format(new Date(campaign.createdAt), 'MMM d, yyyy')}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end space-x-2">
                    {campaign.status === 'draft' && (
                      <button
                        onClick={() => launchCampaign(campaign.id)}
                        disabled={loading}
                        className="p-1 text-gray-400 hover:text-green-600 transition-colors disabled:opacity-50"
                        title="Launch campaign"
                      >
                        <SafeIcon icon={FiPlay} className="text-sm" />
                      </button>
                    )}
                    
                    {campaign.status === 'active' && (
                      <button
                        onClick={() => pauseCampaign(campaign.id)}
                        className="p-1 text-gray-400 hover:text-yellow-600 transition-colors"
                        title="Pause campaign"
                      >
                        <SafeIcon icon={FiPause} className="text-sm" />
                      </button>
                    )}
                    
                    <button
                      onClick={() => onEditCampaign(campaign)}
                      className="p-1 text-gray-400 hover:text-purple-600 transition-colors"
                      title="Edit campaign"
                    >
                      <SafeIcon icon={FiEdit} className="text-sm" />
                    </button>
                    
                    <button
                      onClick={() => deleteCampaign(campaign.id)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete campaign"
                    >
                      <SafeIcon icon={FiTrash2} className="text-sm" />
                    </button>
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CampaignList;